import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import './Booking.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Booking = () => {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    package: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Generate next 14 days (skip weekends)
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    let count = 0;
    let dayOffset = 1;
    
    while (count < 14) {
      const date = new Date(today);
      date.setDate(today.getDate() + dayOffset);
      const dayOfWeek = date.getDay();
      
      // Skip Sundays (0) and Saturdays (6)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        dates.push(date);
        count++;
      }
      dayOffset++;
    }
    return dates;
  };

  const availableDates = generateAvailableDates();

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const formatDateDisplay = (date) => {
    const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    return {
      dayName: days[date.getDay()],
      day: date.getDate(),
      month: months[date.getMonth()]
    };
  };

  useEffect(() => {
    if (selectedDate) {
      fetchAvailability(selectedDate);
    }
  }, [selectedDate]);

  const fetchAvailability = async (date) => {
    setLoadingSlots(true);
    try {
      const response = await axios.get(`${API}/bookings/availability/${date}`);
      setAvailableSlots(response.data.available_slots);
    } catch (error) {
      console.error('Error fetching availability:', error);
      setAvailableSlots([]);
    }
    setLoadingSlots(false);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(formatDate(date));
    setSelectedTime('');
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const handleNextStep = () => {
    if (step === 1 && selectedDate && selectedTime) {
      setStep(2);
      window.scrollTo(0, 0);
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/bookings`, {
        ...formData,
        date: selectedDate,
        time: selectedTime
      });
      setSuccess(true);
      setStep(3);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Es gab einen Fehler. Bitte versuche es erneut.');
    }
    setSubmitting(false);
  };

  const formatSelectedDate = () => {
    if (!selectedDate) return '';
    const date = new Date(selectedDate);
    return date.toLocaleDateString('de-DE', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="booking-page">
      {/* Hero */}
      <section className="booking-hero">
        <div className="booking-hero-bg"></div>
        <div className="booking-hero-content">
          <span className="section-label">Termin buchen</span>
          <h1 className="booking-hero-title">
            Dein <span className="gold">kostenloses</span><br />
            Strategie-Gespräch
          </h1>
          <p className="booking-hero-subtitle">
            30 Minuten, 100% kostenlos und unverbindlich. Lass uns über deine Ziele sprechen 
            und finden, wie Social Media dein Business voranbringen kann.
          </p>
        </div>
      </section>

      {/* Progress Indicator */}
      <div className="booking-progress">
        <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
          <div className="step-circle">1</div>
          <span>Termin wählen</span>
        </div>
        <div className="progress-line"></div>
        <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
          <div className="step-circle">2</div>
          <span>Deine Daten</span>
        </div>
        <div className="progress-line"></div>
        <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
          <div className="step-circle">3</div>
          <span>Bestätigung</span>
        </div>
      </div>

      <section className="booking-section">
        <div className="booking-container">
          {/* Step 1: Date & Time Selection */}
          {step === 1 && (
            <div className="booking-step">
              <div className="step-content">
                <h2 className="step-title">Wähle Datum und Uhrzeit</h2>
                <p className="step-subtitle">
                  Termine verfügbar Montag bis Freitag von 9:00 bis 18:00 Uhr
                </p>

                <div className="date-section">
                  <h3 className="section-subtitle">📅 Datum wählen</h3>
                  <div className="dates-grid">
                    {availableDates.map((date, i) => {
                      const display = formatDateDisplay(date);
                      const dateStr = formatDate(date);
                      return (
                        <button
                          key={i}
                          className={`date-card ${selectedDate === dateStr ? 'selected' : ''}`}
                          onClick={() => handleDateSelect(date)}
                        >
                          <span className="date-day-name">{display.dayName}</span>
                          <span className="date-day">{display.day}</span>
                          <span className="date-month">{display.month}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {selectedDate && (
                  <div className="time-section">
                    <h3 className="section-subtitle">🕐 Uhrzeit wählen</h3>
                    {loadingSlots ? (
                      <div className="loading-slots">
                        <div className="mini-loader"></div>
                        <span>Verfügbare Zeiten werden geladen...</span>
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div className="time-slots-grid">
                        {availableSlots.map((time) => (
                          <button
                            key={time}
                            className={`time-slot ${selectedTime === time ? 'selected' : ''}`}
                            onClick={() => handleTimeSelect(time)}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="no-slots">Keine verfügbaren Zeiten an diesem Tag. Bitte wähle ein anderes Datum.</p>
                    )}
                  </div>
                )}

                {selectedDate && selectedTime && (
                  <div className="selection-summary">
                    <div className="summary-content">
                      <span className="summary-label">Dein gewählter Termin:</span>
                      <span className="summary-value">
                        {formatSelectedDate()} um {selectedTime} Uhr
                      </span>
                    </div>
                    <button className="btn-next" onClick={handleNextStep}>
                      Weiter zur Anmeldung
                      <span className="btn-arrow">→</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Personal Data Form */}
          {step === 2 && (
            <div className="booking-step">
              <div className="step-content">
                <h2 className="step-title">Deine Kontaktdaten</h2>
                <p className="step-subtitle">
                  Damit ich mich vor dem Gespräch optimal auf dich vorbereiten kann.
                </p>

                <div className="appointment-summary">
                  <div className="appointment-icon">📅</div>
                  <div>
                    <div className="appointment-label">Dein Termin</div>
                    <div className="appointment-value">
                      {formatSelectedDate()} um {selectedTime} Uhr
                    </div>
                  </div>
                  <button 
                    className="change-appointment" 
                    onClick={() => setStep(1)}
                  >
                    Ändern
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="booking-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Vor- und Nachname *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        required
                        placeholder="Max Mustermann"
                      />
                    </div>
                    <div className="form-group">
                      <label>E-Mail *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleFormChange}
                        required
                        placeholder="max@example.de"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Telefon *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                      required
                      placeholder="0160 12345678"
                    />
                  </div>

                  <div className="form-group">
                    <label>Worum geht es? (optional)</label>
                    <select
                      name="package"
                      value={formData.package}
                      onChange={handleFormChange}
                    >
                      <option value="">Wähle ein Thema</option>
                      <option value="Allgemeine Beratung">Allgemeine Beratung</option>
                      <option value="Starter Paket">Starter Paket (199€/Monat)</option>
                      <option value="Growth Paket">Growth Paket (249€/Monat)</option>
                      <option value="Pro Paket">Pro Paket (299€/Monat)</option>
                      <option value="Business Paket">Business Paket (349€/Monat)</option>
                      <option value="Premium Paket">Premium Paket (399€/Monat)</option>
                      <option value="Individuelles Angebot">Individuelles Angebot</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Erzähl mir kurz von deinem Projekt (optional)</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleFormChange}
                      rows="4"
                      placeholder="Was ist dein aktueller Stand? Was sind deine Ziele?"
                    ></textarea>
                  </div>

                  <div className="privacy-notice">
                    <input type="checkbox" id="privacy" required />
                    <label htmlFor="privacy">
                      Ich habe die <Link to="/datenschutz" target="_blank">Datenschutzerklärung</Link> gelesen 
                      und stimme der Verarbeitung meiner Daten zu. *
                    </label>
                  </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="btn-back"
                      onClick={() => setStep(1)}
                    >
                      ← Zurück
                    </button>
                    <button 
                      type="submit" 
                      className="btn-submit"
                      disabled={submitting}
                    >
                      {submitting ? 'Wird gesendet...' : 'Termin verbindlich buchen'}
                      <span className="btn-arrow">→</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Step 3: Success */}
          {step === 3 && success && (
            <div className="booking-step success-step">
              <div className="success-icon-large">✓</div>
              <h2 className="success-title">Termin erfolgreich gebucht! 🎉</h2>
              <p className="success-message">
                Vielen Dank, <strong>{formData.name}</strong>! Dein Termin am{' '}
                <strong>{formatSelectedDate()} um {selectedTime} Uhr</strong> ist verbindlich reserviert.
              </p>
              <p className="success-info">
                Ich werde dich kurz vor dem Termin per E-Mail an <strong>{formData.email}</strong> oder 
                telefonisch unter <strong>{formData.phone}</strong> kontaktieren, um den genauen Ablauf 
                zu besprechen.
              </p>

              <div className="success-actions">
                <a 
                  href={`https://wa.me/4901604905213?text=Hallo%20Niels!%20Ich%20habe%20gerade%20einen%20Termin%20für%20${selectedDate}%20um%20${selectedTime}%20Uhr%20gebucht.%20Mein%20Name%20ist%20${encodeURIComponent(formData.name)}.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-whatsapp"
                >
                  💬 Zusätzlich über WhatsApp bestätigen
                </a>
                <Link to="/" className="btn-secondary">
                  Zurück zur Startseite
                </Link>
              </div>

              <div className="next-steps">
                <h3>Was passiert jetzt?</h3>
                <div className="steps-list">
                  <div className="step-item">
                    <div className="step-num">1</div>
                    <div>
                      <strong>Bestätigung</strong>
                      <p>Du erhältst kurz vor dem Termin eine Erinnerung von mir.</p>
                    </div>
                  </div>
                  <div className="step-item">
                    <div className="step-num">2</div>
                    <div>
                      <strong>Unser Gespräch</strong>
                      <p>Wir sprechen 30 Minuten über deine Ziele und Möglichkeiten.</p>
                    </div>
                  </div>
                  <div className="step-item">
                    <div className="step-num">3</div>
                    <div>
                      <strong>Individuelles Angebot</strong>
                      <p>Bei Interesse erstelle ich dir ein maßgeschneidertes Angebot.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Booking;
